/**
 * The commission ledger's arithmetic, in one place: what a contract or a
 * deal has earned, the commission on it, the agent/agency split, expense
 * recoupment, and how an existing ledger row is re-priced. Shared by the
 * app (utils/earnings, utils/commissionAutoRecord) and the scraper's
 * nightly ledger sync (generated copy), so a row the job writes at 3 a.m.
 * is the row the contract page would have written.
 *
 * Rules, stated once:
 *   earned (contract)  completed year → cash payout; cut-guaranteed year →
 *                      guaranteed salary; active year → the pay strip when
 *                      it has periods, else guaranteed salary plus logged
 *                      game checks; plus incentives marked earned with a
 *                      date. Unknown while any year has no status, or any
 *                      period's rate is unknown.
 *   basis (contract)   max(earned, guaranteed floor), or the floor alone
 *                      when earned is unknown; nothing when neither exists.
 *   earned (deal)      paid schedule items; a deal with no schedule is
 *                      priced on its value, labelled guaranteed.
 *   gross              round(base × rate) / 100, to the cent.
 *   split              the agent's dated override, else the agency default;
 *                      none means nothing can be booked (never 50/50).
 *   recoupment         approved or partial expenses of the agent, for this
 *                      client or for no client, oldest first, up to gross;
 *                      applied once, on the row's creation.
 *   re-pricing         expenses already deducted stay (capped at the new
 *                      gross); the split keeps the recorded percentage.
 *
 * Pure, self-contained types. No I/O.
 */

import { yearPay, seasonOf, type PayPeriod, type PayYear, type YearPay } from './payModel.js'

export interface EarnableYear extends PayYear {
  status?: 'future' | 'active' | 'completed' | 'cut_guaranteed' | null
  cash_payout?: number | null
}

export interface EarnableContract {
  commission_rate?: number | null
  total_value?: number | null
  guaranteed_value?: number | null
  accrued_seasons?: number | null
  current_per_game_roster_bonus?: number | null
  yearly_breakdown?: EarnableYear[] | null
  roster_history?: PayPeriod[] | null
  game_log?: { season_year?: number | null; status?: string | null; per_game_amount?: number | null }[] | null
  incentives?: { status?: string | null; earned_date?: string | null; value?: number | null }[] | null
}

export interface ContractEarnedFigure {
  /** Null while the record cannot be priced (no year statuses, or an unknown weekly rate). */
  earned: number | null
  guaranteed_floor: number | null
  pay_years: YearPay[]
  /** Active years with no roster periods, priced from the guarantee and game log instead of week by week. */
  fallback_years: number[]
}

export function contractEarnedFigure(contract: EarnableContract, today: Date = new Date()): ContractEarnedFigure {
  const guaranteed_floor = contract.guaranteed_value ?? null
  const breakdown = contract.yearly_breakdown ?? []
  const pay_years: YearPay[] = []
  const fallback_years: number[] = []
  if (breakdown.length === 0 || !breakdown.some(y => y.status != null)) {
    return { earned: null, guaranteed_floor, pay_years, fallback_years }
  }
  let sum = 0
  let unknownRate = false
  for (const year of breakdown) {
    if (year.status === 'completed') {
      sum += year.cash_payout ?? 0
    } else if (year.status === 'cut_guaranteed') {
      sum += year.guaranteed_salary ?? 0
    } else if (year.status === 'active') {
      const periods = (contract.roster_history ?? []).filter(p => seasonOf(p.start_date) === year.year)
      if (periods.length > 0) {
        const yp = yearPay(year, periods, contract, today)
        pay_years.push(yp)
        if (yp.earned == null) unknownRate = true
        else sum += yp.earned
      } else {
        fallback_years.push(year.year)
        sum += year.guaranteed_salary ?? 0
        for (const game of contract.game_log ?? []) {
          if (game.season_year === year.year && game.status === 'active_53') {
            sum += game.per_game_amount ?? contract.current_per_game_roster_bonus ?? 0
          }
        }
      }
    }
  }
  for (const inc of contract.incentives ?? []) {
    if (inc.status === 'earned' && inc.earned_date) sum += inc.value ?? 0
  }
  return { earned: unknownRate ? null : sum, guaranteed_floor, pay_years, fallback_years }
}

export type Basis = { base: number; basis: 'earned' | 'guaranteed' }

/**
 * What the ledger books commission on for a contract.
 *
 * `basis` names which of the two figures the base actually came from, and it
 * has to, because every caller prints it: the nightly sync writes "Re-priced
 * ... to <basis> money of $X" straight into the row's notes, and that note is
 * the audit trail somebody reads later.
 *
 * It used to say 'earned' whenever an earned figure existed at all, even
 * though the base is the LARGER of earned and the guarantee. Early in a
 * contract the guarantee is nearly always larger, so the rows said "earned
 * money" while carrying commission on the guarantee. On the demo book every
 * one of five contract rows was exactly rate x guaranteed_value - $13,438,579
 * of "fees earned" that nobody had earned yet.
 *
 * The amount is unchanged and deliberate: guaranteed money is owed, so the
 * fee on it is booked. Only the name of the figure is corrected.
 */
export function contractBasis(contract: EarnableContract, today: Date = new Date()): Basis | null {
  const r = contractEarnedFigure(contract, today)
  if (r.earned != null) {
    const floor = r.guaranteed_floor ?? 0
    const base = Math.max(r.earned, floor)
    return { base, basis: base > r.earned ? 'guaranteed' : 'earned' }
  }
  if (r.guaranteed_floor != null) return { base: r.guaranteed_floor, basis: 'guaranteed' }
  return null
}

export interface EarnableDeal {
  value?: number | null
  commission_rate?: number | null
  payment_schedule?: { status?: string | null; amount?: number | null; actual_date?: string | null }[] | null
}

/** Paid schedule items; null when the deal has no schedule or nothing is paid. */
export function dealEarnedFigure(deal: EarnableDeal): { earned: number | null; received: number | null; hasSchedule: boolean } {
  const schedule = deal.payment_schedule ?? []
  const hasSchedule = schedule.length > 0
  const paid = schedule.filter(p => p.status === 'paid')
  if (paid.length === 0) return { earned: null, received: null, hasSchedule }
  const earned = paid.reduce((s, p) => s + (p.amount ?? 0), 0)
  const confirmed = paid.filter(p => p.actual_date != null)
  return { earned, received: confirmed.length ? confirmed.reduce((s, p) => s + (p.amount ?? 0), 0) : null, hasSchedule }
}

/** What the ledger books commission on for an executed deal. */
export function dealBasis(deal: EarnableDeal): Basis | null {
  const r = dealEarnedFigure(deal)
  if (r.hasSchedule) return { base: r.earned ?? 0, basis: 'earned' }
  return deal.value ? { base: deal.value, basis: 'guaranteed' } : null
}

/** Commission to the cent. */
export function grossCommission(base: number, rate: number): number {
  return Math.round(base * rate) / 100
}

export interface SplitRule { agent_percentage: number; agency_percentage: number }
export interface DatedSplit extends SplitRule { agent_id: string; effective_date: string; end_date?: string | null }
export interface AgencySplitSettings { default_agent_split: number; default_agency_split: number }
export type Split = { agentPercentage: number; agencyPercentage: number }

/**
 * The agent's split on a date, else the agency default, else none.
 *
 * Dates compare as calendar days. PocketBase stores a date field as
 * "2026-09-11 00:00:00.000Z", and compared as text against the bare day
 * "2026-09-11" the longer string sorts after it — so a split was treated as
 * not yet in force on the very day it started, and nothing booked until the
 * next morning.
 */
export function effectiveSplit(settings: AgencySplitSettings | null | undefined, agentSplits: DatedSplit[], agentId: string, date: Date = new Date()): Split | null {
  const day = date.toISOString().slice(0, 10)
  const dayOf = (value: string) => String(value).slice(0, 10)
  const own = agentSplits.find(s => s.agent_id === agentId && dayOf(s.effective_date) <= day && (!s.end_date || dayOf(s.end_date) >= day))
  if (own) return { agentPercentage: own.agent_percentage, agencyPercentage: own.agency_percentage }
  if (settings) return { agentPercentage: settings.default_agent_split, agencyPercentage: settings.default_agency_split }
  return null
}

export function applySplit(net: number, split: Split): { agentTake: number; agencyTake: number } {
  return { agentTake: (net * split.agentPercentage) / 100, agencyTake: (net * split.agencyPercentage) / 100 }
}

export interface RecoupableExpense {
  id: string
  agent_id: string
  client_id?: string | null
  amount: number
  status?: string | null
  recouped_amount?: number | null
  expense_date: string
}

/** Expenses that may be recouped from this agent's commission on this client: theirs, approved or partial, for this client or none. */
export function recoupableExpenses(expenses: RecoupableExpense[], agentId: string, clientId: string | null | undefined): RecoupableExpense[] {
  return expenses
    .filter(e => e.agent_id === agentId)
    .filter(e => !clientId || !e.client_id || e.client_id === clientId)
    .filter(e => (e.status === 'approved' || e.status === 'partial') && (e.recouped_amount ?? 0) < e.amount)
    .sort((a, b) => a.expense_date.localeCompare(b.expense_date))
}

export function unrecoupedTotal(expenses: RecoupableExpense[], agentId: string, clientId: string | null | undefined): number {
  return recoupableExpenses(expenses, agentId, clientId).reduce((s, e) => s + (e.amount - (e.recouped_amount ?? 0)), 0)
}

export interface RecoupmentStep { expenseId: string; take: number; newRecouped: number; fullyRecouped: boolean }

/** Oldest first, up to `amount`: what each expense would have taken from it. */
export function recoupmentPlan(expenses: RecoupableExpense[], agentId: string, clientId: string | null | undefined, amount: number): RecoupmentStep[] {
  const steps: RecoupmentStep[] = []
  let remaining = amount
  for (const e of recoupableExpenses(expenses, agentId, clientId)) {
    if (remaining <= 0) break
    const open = e.amount - (e.recouped_amount ?? 0)
    const take = Math.min(open, remaining)
    if (take <= 0) continue
    const newRecouped = (e.recouped_amount ?? 0) + take
    steps.push({ expenseId: e.id, take, newRecouped, fullyRecouped: newRecouped >= e.amount })
    remaining -= take
  }
  return steps
}

export interface LedgerAmounts { gross_amount: number; expenses_deducted: number; net_amount: number; agent_share: number; agency_share: number }

/** A new row's amounts: gross, recoupment capped at gross, the split on the net. */
export function newLedgerAmounts(base: number, rate: number, split: Split, unrecouped: number, recoupmentEnabled: boolean): LedgerAmounts & { split_percentage: number } {
  const gross = (base * rate) / 100
  const expenses_deducted = recoupmentEnabled ? Math.min(unrecouped, gross) : 0
  const net = gross - expenses_deducted
  const { agentTake, agencyTake } = applySplit(net, split)
  return { gross_amount: gross, expenses_deducted, net_amount: net, agent_share: agentTake, agency_share: agencyTake, split_percentage: split.agentPercentage }
}

/** An existing row re-priced: expenses already deducted stay (capped), the recorded split holds. */
export function reconciledAmounts(entry: { expenses_deducted: number | null; split_percentage: number | null }, gross: number): LedgerAmounts {
  const g = Math.max(0, Math.round(gross * 100) / 100)
  const expenses = Math.min(entry.expenses_deducted ?? 0, g)
  const net = g - expenses
  const agentPct = entry.split_percentage ?? 0
  const agent_share = Math.round(net * agentPct) / 100
  return { gross_amount: g, expenses_deducted: expenses, net_amount: net, agent_share, agency_share: Math.round((net - agent_share) * 100) / 100 }
}

/** Two money amounts are the same to the cent. */
export function sameMoney(a: number | null | undefined, b: number | null | undefined): boolean {
  return Math.abs((a ?? 0) - (b ?? 0)) < 0.005
}

/** "2026-Q3" for a date. */
export function periodOf(dateIso: string): string {
  const d = new Date(dateIso)
  return `${d.getUTCFullYear()}-Q${Math.floor(d.getUTCMonth() / 3) + 1}`
}
